import { api } from "../config/api";
import { post } from "../api";
import {
  buildPresignedUploadFormData,
  isPresignedUploadNoContentSuccess,
  postPresignedMultipartForm,
} from "../utils/http/presignedMultipartUpload";
import { feedbackService } from "./feedback.service";
import type {
  MyProfileSessionPayload,
  MyProfileUserDetails,
  MyProfilePhoneItem,
  MyProfileAddressItem,
  MyProfileIdProof,
  TermsItem,
  GetUserDetailsResponse,
  GetUserPhoneNumbersResponse,
  GetUserAddressesResponse,
  GetUserIdProofsResponse,
  GetUserIdProofsApiItem,
  GetTermsStatusCheckResponse,
} from "../components/MainContent/pages/MyProfile/types";
import {
  displayNameFromMyProfileUserDetails,
  parseIdProofDocumentPath,
} from "../components/MainContent/pages/MyProfile/utils/parseIdProofDocumentPath";
import { selectCurrentUserId, isSameUserId } from "../store/authSelectors";
import type { RootState } from "../types/store";

function ensureArray<T>(val: unknown): T[] {
  return Array.isArray(val) ? (val as T[]) : [];
}

function getLoggedInUserId(): string | null {
  try {
    const { store } = require("../store") as { store: { getState: () => RootState } };
    return selectCurrentUserId(store.getState());
  } catch {
    return null;
  }
}

export function resolveProfileUpdatedBy(forUserId?: string): "self" | "business" {
  const targetUserId = forUserId?.trim();
  if (!targetUserId) return "self";
  const loggedInUserId = getLoggedInUserId();
  if (loggedInUserId && isSameUserId(loggedInUserId, targetUserId)) return "self";
  return "business";
}

export function resolveProfileSubjectUserId(forUserId?: string): string | undefined {
  if (resolveProfileUpdatedBy(forUserId) !== "business") return undefined;
  return forUserId?.trim() || undefined;
}

export function enrichIdProofsWithUserDetails(
  proofs: MyProfileIdProof[],
  userDetails: MyProfileUserDetails | null
): MyProfileIdProof[] {
  const profileUserId = userDetails?.user_id;
  const displayName = displayNameFromMyProfileUserDetails(userDetails);
  if (profileUserId === undefined || profileUserId === null || !displayName) return proofs;

  return proofs.map((proof) => {
    if (proof.uploaded_by) return proof;
    const path = proof.file_key ?? proof.idproof;
    const parsed = parseIdProofDocumentPath(path);
    if (parsed?.userId === profileUserId) {
      return { ...proof, uploaded_by: displayName };
    }
    return proof;
  });
}

export async function getUserDetails(
  payload: MyProfileSessionPayload,
  options?: { forUserId?: string }
): Promise<MyProfileUserDetails | null> {
  const { device_token, session_id, business_id } = payload;
  if (!device_token || !session_id || !business_id) return null;
  const body: Record<string, unknown> = {
    device_token,
    session_id,
    business_id,
  };
  const uid = options?.forUserId?.trim();
  if (uid) {
    body.user_id = uid;
  }
  const res = await post<GetUserDetailsResponse>(api.GET_USER_DETAILS, body);
  if (res?.success && res?.data?.user_details) return res.data.user_details;
  return null;
}

export async function getUserPhoneNumbers(
  payload: MyProfileSessionPayload,
  options?: { forUserId?: string }
): Promise<MyProfilePhoneItem[]> {
  const { device_token, session_id, business_id } = payload;
  if (!device_token || !session_id || !business_id) return [];
  const body: Record<string, unknown> = {
    device_token,
    session_id,
    business_id,
  };
  const uid = options?.forUserId?.trim();
  if (uid) {
    body.user_id = uid;
  }
  const res = await post<GetUserPhoneNumbersResponse>(api.GET_USER_PHONE_NUMBERS, body);
  if (!res?.success || !res?.data) return [];
  const list = ensureArray<MyProfilePhoneItem>(res.data.phonenumbers);
  const log = ensureArray<MyProfilePhoneItem>(res.data.phonenumbers_log);
  return [...list, ...log];
}

export async function getUserAddresses(
  payload: MyProfileSessionPayload,
  options?: { forUserId?: string }
): Promise<MyProfileAddressItem[]> {
  const { device_token, session_id, business_id } = payload;
  if (!device_token || !session_id || !business_id) return [];
  const body: Record<string, unknown> = {
    device_token,
    session_id,
    business_id,
  };
  const uid = options?.forUserId?.trim();
  if (uid) {
    body.user_id = uid;
  }
  const res = await post<GetUserAddressesResponse>(api.GET_USER_ADDRESSES, body);
  if (!res?.success || !res?.data) return [];
  const list = ensureArray<MyProfileAddressItem>(res.data.addresses);
  const log = ensureArray<MyProfileAddressItem>(res.data.addresses_log);
  const merged: MyProfileAddressItem[] = [...list, ...log].map((a) => ({
    ...a,
    full_address: a.address ?? a.full_address,
  }));
  return merged;
}

export async function getUserIdProofs(
  payload: MyProfileSessionPayload,
  options?: { forUserId?: string }
): Promise<MyProfileIdProof[]> {
  const { device_token, session_id, business_id } = payload;
  if (!device_token || !session_id || !business_id) return [];
  const body: Record<string, unknown> = {
    device_token,
    session_id,
    business_id,
  };
  const uid = options?.forUserId?.trim();
  if (uid) {
    body.user_id = uid;
  }
  const res = await post<GetUserIdProofsResponse>(api.GET_USER_ID_PROOFS, body);
  if (!res?.success || !res?.data?.idproofs) return [];
  const raw = ensureArray<GetUserIdProofsApiItem>(res.data.idproofs);
  return raw.map((item): MyProfileIdProof => {
    const parsedPath = parseIdProofDocumentPath(item.document);
    const uploadedBy =
      item.uploaded_by ??
      item.uploaded_by_name ??
      item.created_by_name ??
      (typeof item.created_by === "string" ? item.created_by : undefined);
    const uploadedOn =
      item.uploaded_on ?? item.uploaded_at ?? item.created_on ?? parsedPath?.uploadedOnIsoDate;
    const ext = item as Record<string, unknown>;
    const rawDocId =
      item.document_id ?? ext.id ?? ext.proof_id ?? ext.proof_document_id ?? ext.documentId;
    const numericId =
      typeof rawDocId === "number"
        ? rawDocId
        : typeof rawDocId === "string" && /^\d+$/.test(rawDocId.trim())
          ? Number(rawDocId.trim())
          : rawDocId;
    return {
      id: numericId as number | undefined,
      document_id: numericId as number | undefined,
      idproof: item.document,
      file_key: item.document,
      doc_name: item.name,
      proof_type: item.proof_type,
      uploaded_by: uploadedBy,
      uploaded_on: uploadedOn,
    };
  });
}

export async function getTermsStatusCheck(
  payload: MyProfileSessionPayload,
  options?: { forUserId?: string }
): Promise<TermsItem[]> {
  const { device_token, session_id, business_id } = payload;
  if (!device_token || !session_id) return [];
  const body: Record<string, unknown> = {
    device_token,
    session_id,
    business_id,
  };
  const uid = options?.forUserId?.trim();
  if (uid) {
    body.user_id = uid;
  }
  const res = await post<GetTermsStatusCheckResponse>(api.GET_TERMS_STATUS_CHECK, body);
  if (res?.success && res?.data?.terms_info?.terms_accept) {
    return ensureArray<TermsItem>(res.data.terms_info.terms_accept);
  }
  return [];
}

export interface UploadProfilePictureResponse {
  success?: boolean;
  data?: {
    profile_picture?: { username?: string; profile_picture?: string };
  };
  errors?: string | string[];
}

export async function uploadProfilePicture(
  payload: MyProfileSessionPayload,
  file: File
): Promise<string | null> {
  const { device_token, session_id, business_id } = payload;
  if (!device_token || !session_id || !business_id) return null;
  const uploadInfo = await feedbackService.getUploadInfo({
    doc_type: "profile_picture",
    doc_name: file.name,
    doc_extra_info: "",
    device_token,
    session_id,
    business_id,
  });
  if (!uploadInfo?.success || !uploadInfo.data?.upload_info) return null;
  const formData = buildPresignedUploadFormData(uploadInfo.data.upload_info.fields, file);
  const uploadResp = await postPresignedMultipartForm(uploadInfo.data.upload_info.url, formData);
  if (!isPresignedUploadNoContentSuccess(uploadResp)) return null;
  const key = uploadInfo.data.upload_info.fields?.key;
  const uuid = uploadInfo.data.upload_info.uuid;
  if (!key || !uuid) return null;
  const res = await post<UploadProfilePictureResponse>(api.UPLOAD_PROFILE_PIC, {
    device_token,
    session_id,
    business_id,
    profilepic: key,
    uuid,
  });
  if (res?.success && res?.data?.profile_picture?.profile_picture) {
    return res.data.profile_picture.profile_picture;
  }
  return null;
}

export interface ChangePasswordResponse {
  success?: boolean;
  message?: string;
  data?: {
    user_info?: { username?: string; password_changed?: boolean | string | number };
    errors?: string | string[];
    message?: string;
  };
  errors?: string | string[];
}

export type ChangePasswordResult = { ok: true } | { ok: false; message: string };

function messageFromApiErrors(errors: string | string[] | null | undefined): string | undefined {
  if (errors === undefined || errors === null) return undefined;
  if (Array.isArray(errors)) {
    const parts = errors.filter((e): e is string => typeof e === "string" && e.trim() !== "");
    return parts.length ? parts.join(", ") : undefined;
  }
  const s = errors.trim();
  return s || undefined;
}

function isPasswordMarkedChanged(value: unknown): boolean {
  return value === true || value === "true" || value === 1;
}

function failureMessageFromChangePasswordResponse(
  res: ChangePasswordResponse | undefined
): string | undefined {
  if (!res) return undefined;
  const fromTop = messageFromApiErrors(res.errors);
  if (fromTop) return fromTop;
  const fromData = messageFromApiErrors(res.data?.errors);
  if (fromData) return fromData;
  if (res.success === true) {
    return undefined;
  }
  const rootMsg = typeof res.message === "string" ? res.message.trim() : "";
  if (rootMsg) return rootMsg;
  const dataMsg = typeof res.data?.message === "string" ? res.data.message.trim() : "";
  return dataMsg || undefined;
}

export async function changePassword(
  payload: MyProfileSessionPayload,
  oldpassword: string,
  newpassword: string
): Promise<ChangePasswordResult> {
  const { device_token, session_id, business_id } = payload;
  if (!device_token || !session_id || !business_id) {
    return { ok: false, message: "Missing session. Please refresh and try again." };
  }
  const oldTrim = oldpassword?.trim() ?? "";
  const newTrim = newpassword?.trim() ?? "";
  if (!newTrim) {
    return { ok: false, message: "New password is required." };
  }
  const res = await post<ChangePasswordResponse>(api.CHANGE_PASSWORD, {
    oldpassword: oldTrim,
    newpassword: newTrim,
    device_token,
    session_id,
    business_id,
  });

  const failureMsg = failureMessageFromChangePasswordResponse(res);
  if (failureMsg) {
    return { ok: false, message: failureMsg };
  }

  if (res?.success !== true) {
    return { ok: false, message: "Failed to update password. Please try again." };
  }

  const marked = res?.data?.user_info?.password_changed;
  if (!isPasswordMarkedChanged(marked)) {
    return {
      ok: false,
      message: "Your password could not be updated. Please try again.",
    };
  }

  return { ok: true };
}

export interface CreatePhoneNumberPayload extends MyProfileSessionPayload {
  phone: string;
  country_code: string;
  phone_type: string;
  is_active: boolean;
  updated_by?: string;
}

export interface CreatePhoneNumberResponse {
  success?: boolean;
  data?: {
    phone_number_info?: {
      phone_id?: number;
      phone_number?: string;
      phone_type?: string;
      is_active?: boolean;
      updated_by?: string;
      user?: string;
    };
  };
  errors?: string | string[];
}

export async function createPhoneNumber(
  payload: MyProfileSessionPayload,
  params: {
    phone: string;
    country_code: string;
    phone_type: string;
    is_active: boolean;
    forUserId?: string;
  }
): Promise<CreatePhoneNumberResponse> {
  const { device_token, session_id, business_id } = payload;
  const { phone, country_code, phone_type, is_active, forUserId } = params;
  if (!device_token || !session_id || !business_id || !phone?.trim() || !phone_type) {
    return { success: false, errors: "Missing required fields" };
  }
  const normalizedCountryCode = country_code.startsWith("+")
    ? country_code
    : `+${country_code || "91"}`;
  const body: Record<string, unknown> = {
    phone: phone.trim().replace(/\D/g, ""),
    country_code: normalizedCountryCode,
    phone_type,
    is_active: !!is_active,
    business_id,
    device_token,
    session_id,
    updated_by: resolveProfileUpdatedBy(forUserId),
  };
  const subjectUserId = resolveProfileSubjectUserId(forUserId);
  if (subjectUserId) {
    body.user_id = subjectUserId;
  }
  return post<CreatePhoneNumberResponse>(api.CREATE_PHONE_NUMBER, body);
}

export async function updatePhoneNumber(
  payload: MyProfileSessionPayload,
  params: {
    phone_id: string | number;
    phone: string;
    country_code: string;
    phone_type: string;
    is_active: boolean;
    forUserId?: string;
  }
): Promise<CreatePhoneNumberResponse> {
  const { device_token, session_id, business_id } = payload;
  const { phone_id, phone, country_code, phone_type, is_active, forUserId } = params;
  if (
    !device_token ||
    !session_id ||
    !business_id ||
    phone_id === undefined ||
    phone_id === null ||
    !phone?.trim() ||
    !phone_type
  ) {
    return { success: false, errors: "Missing required fields" };
  }
  const normalizedCountryCode = country_code.startsWith("+")
    ? country_code
    : `+${country_code || "91"}`;
  const body: Record<string, unknown> = {
    business_id,
    device_token,
    session_id,
    phone_id,
    phone: phone.trim().replace(/\D/g, ""),
    country_code: normalizedCountryCode,
    phone_type,
    is_active: !!is_active,
    updated_by: resolveProfileUpdatedBy(forUserId),
  };
  const subjectUserId = resolveProfileSubjectUserId(forUserId);
  if (subjectUserId) {
    body.user_id = subjectUserId;
  }
  return post<CreatePhoneNumberResponse>(api.CREATE_PHONE_NUMBER, body);
}

export async function deletePhoneNumber(
  payload: MyProfileSessionPayload,
  params: {
    phone_id: string | number;
    phone?: string;
    country_code?: string;
    phone_type?: string;
    forUserId?: string;
  }
): Promise<CreatePhoneNumberResponse> {
  const { device_token, session_id, business_id } = payload;
  const { phone_id, phone, country_code, phone_type, forUserId } = params;
  if (!device_token || !session_id || !business_id || phone_id === undefined || phone_id === null) {
    return { success: false, errors: "Missing required fields" };
  }
  const normalizedCountryCode =
    country_code !== undefined && country_code !== null && country_code !== ""
      ? country_code.startsWith("+")
        ? country_code
        : `+${country_code}`
      : "+91";
  const body: Record<string, unknown> = {
    business_id,
    device_token,
    session_id,
    phone_id,
    phone: phone ?? "",
    country_code: normalizedCountryCode,
    phone_type: phone_type ?? "other",
    is_active: false,
    updated_by: resolveProfileUpdatedBy(forUserId),
  };
  const subjectUserId = resolveProfileSubjectUserId(forUserId);
  if (subjectUserId) {
    body.user_id = subjectUserId;
  }
  return post<CreatePhoneNumberResponse>(api.CREATE_PHONE_NUMBER, body);
}

export interface AddressApiResponse {
  success?: boolean;
  data?: { address_info?: { address_id?: number; full_address?: string; address_type?: string } };
  errors?: string | string[];
}

export async function createAddress(
  payload: MyProfileSessionPayload,
  params: {
    full_address: string;
    address_type: string;
    is_active?: boolean;
    forUserId?: string;
  }
): Promise<AddressApiResponse> {
  const { device_token, session_id, business_id } = payload;
  const { full_address, address_type, is_active = true, forUserId } = params;
  if (!device_token || !session_id || !business_id || !full_address?.trim() || !address_type) {
    return { success: false, errors: "Missing required fields" };
  }
  const subjectUserId = resolveProfileSubjectUserId(forUserId);
  const body: Record<string, unknown> = {
    address_id: "",
    full_address: full_address.trim(),
    address_type: address_type.trim().toLowerCase(),
    is_active: !!is_active,
    device_token,
    session_id,
    client_user_id: subjectUserId ?? "",
    updated_by: resolveProfileUpdatedBy(forUserId),
    business_id,
  };
  if (subjectUserId) {
    body.user_id = subjectUserId;
  }
  return post<AddressApiResponse>(api.CREATE_ADDRESS, body);
}

export async function updateAddress(
  payload: MyProfileSessionPayload,
  params: {
    address_id: string | number;
    full_address: string;
    address_type: string;
    is_active?: boolean;
    forUserId?: string;
  }
): Promise<AddressApiResponse> {
  const { device_token, session_id, business_id } = payload;
  const { address_id, full_address, address_type, is_active = true, forUserId } = params;
  if (
    !device_token ||
    !session_id ||
    !business_id ||
    address_id === undefined ||
    address_id === null ||
    address_id === "" ||
    !full_address?.trim() ||
    !address_type
  ) {
    return { success: false, errors: "Missing required fields" };
  }
  const subjectUserId = resolveProfileSubjectUserId(forUserId);
  const body: Record<string, unknown> = {
    address_id,
    full_address: full_address.trim(),
    address_type: address_type.trim().toLowerCase(),
    is_active: !!is_active,
    device_token,
    session_id,
    client_user_id: subjectUserId ?? "",
    updated_by: resolveProfileUpdatedBy(forUserId),
    business_id,
  };
  if (subjectUserId) {
    body.user_id = subjectUserId;
  }
  return post<AddressApiResponse>(api.CREATE_ADDRESS, body);
}

export async function deleteAddress(
  payload: MyProfileSessionPayload,
  params: {
    address_id: string | number;
    full_address?: string;
    address_type?: string;
    forUserId?: string;
  }
): Promise<AddressApiResponse> {
  const { device_token, session_id, business_id } = payload;
  const { address_id, full_address, address_type, forUserId } = params;
  if (
    !device_token ||
    !session_id ||
    !business_id ||
    address_id === undefined ||
    address_id === null ||
    address_id === ""
  ) {
    return { success: false, errors: "Missing required fields" };
  }
  const subjectUserId = resolveProfileSubjectUserId(forUserId);
  const body: Record<string, unknown> = {
    address_id,
    full_address: full_address ?? "",
    address_type: address_type ?? "other",
    is_active: false,
    device_token,
    session_id,
    client_user_id: subjectUserId ?? "",
    updated_by: resolveProfileUpdatedBy(forUserId),
    business_id,
  };
  if (subjectUserId) {
    body.user_id = subjectUserId;
  }
  return post<AddressApiResponse>(api.CREATE_ADDRESS, body);
}

export interface GetUploadInfoForProofPayload extends MyProfileSessionPayload {
  doc_name: string;
  forUserId?: string;
}

export interface GetUploadInfoForProofResponse {
  success?: boolean;
  data?: {
    upload_info?: {
      url: string;
      fields: Record<string, string>;
      file_name?: string;
      uuid: string;
    };
  };
  errors?: string | string[];
}

export async function getUploadInfoForProof(
  payload: GetUploadInfoForProofPayload
): Promise<GetUploadInfoForProofResponse | null> {
  const { device_token, session_id, business_id, doc_name, forUserId } = payload;
  if (!device_token || !session_id || !business_id || !doc_name) return null;
  const subjectUserId = resolveProfileSubjectUserId(forUserId);
  return post<GetUploadInfoForProofResponse>(api.GET_UPLOAD_INFO, {
    doc_type: "proof",
    doc_name,
    doc_extra_info: "",
    device_token,
    session_id,
    business_id,
    client_user_id: subjectUserId ?? "",
  });
}

export interface UploadIdProofPayload extends MyProfileSessionPayload {
  idproof: string;
  proof_type: string;
  doc_name: string;
  uuid: string;
  forUserId?: string;
}

export interface UploadIdProofResponse {
  success?: boolean;
  data?: { id_proof?: { user?: string; idproof?: string; doc_name?: string } };
  errors?: string | string[];
}

export async function uploadIdProof(payload: UploadIdProofPayload): Promise<UploadIdProofResponse> {
  const { device_token, session_id, business_id, idproof, proof_type, doc_name, uuid, forUserId } =
    payload;
  if (
    !device_token ||
    !session_id ||
    !business_id ||
    !idproof ||
    !proof_type ||
    !doc_name ||
    !uuid
  ) {
    return { success: false, errors: "Missing required fields" };
  }
  const subjectUserId = resolveProfileSubjectUserId(forUserId);
  const body: Record<string, unknown> = {
    idproof,
    proof_type,
    doc_name,
    device_token,
    session_id,
    uuid,
    updated_by: resolveProfileUpdatedBy(forUserId),
    client_user_id: subjectUserId ?? "",
    business_id,
  };
  if (subjectUserId) {
    body.user_id = subjectUserId;
  }
  return post<UploadIdProofResponse>(api.UPLOAD_ID_PROOF, body);
}

export const myProfileService = {
  getUserDetails,
  getUserPhoneNumbers,
  getUserAddresses,
  getUserIdProofs,
  enrichIdProofsWithUserDetails,
  getTermsStatusCheck,
  uploadProfilePicture,
  changePassword,
  createPhoneNumber,
  updatePhoneNumber,
  deletePhoneNumber,
  createAddress,
  updateAddress,
  deleteAddress,
  getUploadInfoForProof,
  uploadIdProof,
};
